import React, { useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import ThreadsListDesktop from './threads/ThreadsListDesktop';
import { DesktopChatPanel } from './DesktopChatPanel';
import { useLocalStorageState } from '@/contexts/ai-agents/LocalStorageStateContext';
import { useUser } from '@/hooks/auth/use-user';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { useChatManager } from '@/hooks/ai-agents/useChatManager';
import { useManualCacheRefresh } from '@/hooks/navigation/useProjectsPagesDatabaseCache';

interface DesktopChatLayoutProps {
  onClose: () => void;
}

/**
 * DesktopChatLayout - Complete two-panel desktop layout
 * 
 * Layout:
 * - Left Panel (320px): Threads sidebar with conversations list
 * - Right Panel (flexible): Chat interface or empty state
 * 
 * Features:
 * - Proper sizing for desktop screens
 * - Clean threads list focused on action buttons
 * - Responsive thread selection
 */
export const DesktopChatLayout: React.FC<DesktopChatLayoutProps> = ({ onClose }) => {
  const { setCurrentThreadId } = useLocalStorageState();
  const { user } = useUser();
  const { currentAssistant } = useAIAgentChat();
  const chatManager = useChatManager(user, currentAssistant);
  const { refreshCache } = useManualCacheRefresh();

  // 🔄 CACHE REFRESH: Refresh AI context when desktop chat initializes
  useEffect(() => {
    const refreshAIContext = async () => {
      try {
        console.log('[DesktopChatLayout] Refreshing AI context cache on initialization...');
        await refreshCache();
        console.log('[DesktopChatLayout] AI context cache refreshed successfully');
      } catch (error) {
        // Cache refresh failure shouldn't block UI - log and continue
        console.warn('[DesktopChatLayout] Cache refresh failed (non-blocking):', error);
      }
    };

    // Only refresh if we have a user (avoid unnecessary calls)
    if (user?.id) {
      refreshAIContext();
    }
  }, [user?.id, refreshCache]);

  // ✅ DESKTOP COMPLETE: Handle both thread selection and creation
  const handleDesktopThreadSelect = useCallback(async (threadId?: string) => {
    try {
      if (threadId) {
        // ✅ EXISTING THREAD: Direct localStorage update (fast path)
        setCurrentThreadId(threadId);
        // ChatManager automatically detects change and loads messages
      } else {
        // ✅ NEW THREAD: Complete creation workflow
        console.log('[DesktopChatLayout] Creating new thread via ChatManager');
        
        // Use ChatManager to create new thread (same as mobile)
        const newThreadId = await chatManager.loadThread();
        
        if (newThreadId) {
          // Set the new thread ID (triggers UI updates)
          setCurrentThreadId(newThreadId);
          
          // ✅ CACHE INVALIDATION: Emit event for threads list refresh
          window.dispatchEvent(new CustomEvent('localStorage-change', {
            detail: { key: 'newThreadCreated', threadId: newThreadId }
          }));
          
          console.log('[DesktopChatLayout] New thread created successfully:', newThreadId);
        } else {
          console.error('[DesktopChatLayout] Failed to create new thread');
        }
      }
    } catch (error) {
      console.error('[DesktopChatLayout] Thread operation failed:', error);
    }
  }, [setCurrentThreadId, chatManager]);

  return (
    <div className="w-full h-full flex overflow-hidden bg-background rounded-lg">
      {/* Left Panel - Threads Sidebar */}
      <div className="w-80 flex-shrink-0 border-r border-border/20 bg-background/50 backdrop-blur-sm flex flex-col h-full">
        {/* Threads List - takes full space */}
        <div className="flex-1 min-h-0">
          <ThreadsListDesktop 
            onThreadSelect={handleDesktopThreadSelect}
          />
        </div>
      </div>
      
      {/* Right Panel - Chat Interface */}
      <div className="flex-1 min-w-0 flex flex-col h-full">
        <DesktopChatPanel onClose={onClose} />
      </div>
    </div>
  );
}; 